import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../../../theme/ThemeContext';
import { StaffFormRow } from '../index';
import { StaffCredentialsModal } from '../StaffCredentialsModal';
import { ConfirmationModal } from '../../common';
import useDepartmentStore from '../../../stores/departmentStore';
import { createEmptyStaffRow, STAFF_ROLE_LABELS, STAFF_STATUS_LABELS, STAFF_STATUS, SPECIALIZATION_LABELS } from '../../../constants/staff';
import { validateStaffList, validateStaffRow } from '../../../utils/staffValidation';
import {
  fetchStaffList, bulkCreateStaff, updateStaff, disableStaff, enableStaff,
  getDoctorSignature, updateDoctorSignature,
} from '../../../services/api/staffApi';
import './ManageStaffModal.css';

/**
 * ManageStaffModal
 *
 * Full staff management overlay: list existing staff, edit, disable/enable,
 * and add new staff using the same reusable StaffFormRow component.
 *
 * Props:
 *   isOpen       — boolean, controls visibility
 *   onClose      — () => void, called to dismiss the modal
 *   onStaffAdded — (credentials) => void, called after staff created successfully
 */
function ManageStaffModal({ isOpen, onClose, onStaffAdded, preventClose = false }) {
  const { theme } = useTheme();

  // ── List state ────────────────────────────────────────────────────
  const [staffList, setStaffList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // ── Add form state ────────────────────────────────────────────────
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStaffRows, setNewStaffRows] = useState([createEmptyStaffRow()]);
  const [newRowErrors, setNewRowErrors] = useState([{}]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [createdCredentials, setCreatedCredentials] = useState([]);

  // ── Edit state ────────────────────────────────────────────────────
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  // Doctor Rx signature — hydrated separately via GET .../signature (its
  // own read/write surface, not part of the generic staff fields above).
  const [editSignatureLoading, setEditSignatureLoading] = useState(false);

  // ── Disable/Enable state ─────────────────────────────────────────
  const [togglingId, setTogglingId] = useState(null);

  // ── Confirm dialog state (replaces window.confirm) ────────────────
  const [confirmDialog, setConfirmDialog] = useState(null);

  // ── Error alert state (replaces alert()) ──────────────────────────
  const [errorAlert, setErrorAlert] = useState(null);

  // ── Scroll ref ────────────────────────────────────────────────────
  const bodyRef = useRef(null);

  // Guards the async signature-hydration fetch in startEdit against a race:
  // if the admin cancels one doctor's edit and opens another's before the
  // first GET .../signature resolves, this stops the stale result from
  // landing on the wrong row.
  const activeEditIdRef = useRef(null);

  const scrollBodyToTop = useCallback(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // ── Fetch staff list ──────────────────────────────────────────────

  const loadStaff = useCallback(async () => {
    setIsLoading(true);
    setListError(null);

    const result = await fetchStaffList();
    if (result.success) {
      setStaffList(result.data);
    } else {
      setListError(result.error);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadStaff();
      // Scroll modal body to top when opened
      setTimeout(scrollBodyToTop, 100);
    }
  }, [isOpen, loadStaff, scrollBodyToTop]);

  // ── Optional Department picker (single source of truth: departmentStore) ──
  const departments = useDepartmentStore((s) => s.departments);
  const deptsLoaded = useDepartmentStore((s) => s.deptsLoaded);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);

  // Fetch the dept list ONCE, only while the modal is open. Guarded by
  // deptsLoaded (store dedups) with stable primitive deps — no request storm.
  useEffect(() => {
    if (isOpen && !deptsLoaded) fetchDepartments();
  }, [isOpen, deptsLoaded, fetchDepartments]);

  // ── Add Staff ─────────────────────────────────────────────────────

  const openAddForm = () => {
    setShowAddForm(true);
    setNewStaffRows([createEmptyStaffRow()]);
    setNewRowErrors([{}]);
    setSubmitError(null);
    // Scroll to top so the add form is visible
    setTimeout(scrollBodyToTop, 50);
  };

  const closeAddForm = () => {
    setShowAddForm(false);
    setNewStaffRows([createEmptyStaffRow()]);
    setNewRowErrors([{}]);
    setSubmitError(null);
  };

  const addNewRow = () => {
    setNewStaffRows((prev) => [...prev, createEmptyStaffRow()]);
    setNewRowErrors((prev) => [...prev, {}]);
  };

  const removeNewRow = (index) => {
    if (newStaffRows.length <= 1) return;
    setNewStaffRows((prev) => prev.filter((_, i) => i !== index));
    setNewRowErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNewFieldChange = (index, field, value) => {
    setNewStaffRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setNewRowErrors((prev) => {
      const updated = [...prev];
      if (updated[index]?.[field]) {
        updated[index] = { ...updated[index] };
        delete updated[index][field];
      }
      return updated;
    });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const { rowErrors: errors, hasErrors } = validateStaffList(newStaffRows);
    setNewRowErrors(errors);
    if (hasErrors) return;

    setIsSubmitting(true);
    const result = await bulkCreateStaff(newStaffRows);
    setIsSubmitting(false);

    if (result.success) {
      // Show credentials modal for newly created staff
      const created = result.data?.created || [];
      if (created.length > 0) {
        setCreatedCredentials(created);
      }
      closeAddForm();
      loadStaff();
      // Notify parent (e.g. to update staff_onboarded flag)
      if (onStaffAdded) onStaffAdded(result.data);
    } else {
      setSubmitError(result.error);
    }
  };

  // ── Edit Staff ────────────────────────────────────────────────────

  const startEdit = (staff) => {
    const memberId = staff.member_id || staff.staff_id || staff.id;
    activeEditIdRef.current = memberId;
    setEditingId(memberId);
    setEditData({
      first_name: staff.first_name || '',
      last_name: staff.last_name || '',
      mobile: staff.mobile || '',
      email: staff.email || '',
      role: staff.role || 'DOCTOR',
      specialization: staff.specialization || '',
      registration_no: staff.registration_no || '',
      signature_html: '',
      signature_font_family: null,
      signature_font_size: null,
    });
    setEditErrors({});

    // Hydrate the doctor's signature text separately — one GET call gives
    // the SignatureEditor the fetched text with no HTML parsing needed
    // anywhere on the frontend.
    if (staff.role === 'DOCTOR') {
      setEditSignatureLoading(true);
      getDoctorSignature(memberId).then((result) => {
        if (activeEditIdRef.current !== memberId) return; // stale — edit target changed
        if (result.success) {
          setEditData((prev) => (prev ? {
            ...prev,
            signature_html: result.data?.signature_html || '',
            signature_font_family: result.data?.font_family || null,
            signature_font_size: result.data?.font_size || null,
          } : prev));
        }
        setEditSignatureLoading(false);
      });
    }
  };

  const cancelEdit = () => {
    activeEditIdRef.current = null;
    setEditingId(null);
    setEditData(null);
    setEditErrors({});
    setEditSignatureLoading(false);
  };

  const handleEditFieldChange = (_, field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
    setEditErrors((prev) => {
      if (prev[field]) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return prev;
    });
  };

  const saveEdit = async () => {
    // signature_html is NOT part of the generic staff PUT —
    // StaffMemberUpdateRequest deliberately excludes it (extra="forbid")
    // so the signature's own role check can't be bypassed via this path.
    const { signature_html, signature_font_family, signature_font_size, ...staffFields } = editData;

    const errors = validateStaffRow(staffFields);
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsUpdating(true);
    const result = await updateStaff(editingId, staffFields);

    if (!result.success) {
      setIsUpdating(false);
      setEditErrors({ _server: result.error });
      return;
    }

    // Sequential, not parallel: the signature save only happens once the
    // staff-fields save has actually succeeded, and its failure is reported
    // distinctly rather than as a blanket success/failure — the staff
    // details are already saved at this point regardless of what happens next.
    if (staffFields.role === 'DOCTOR') {
      const sigResult = await updateDoctorSignature(editingId, signature_html || '', {
        fontFamily: signature_font_family, fontSize: signature_font_size,
      });
      setIsUpdating(false);
      if (!sigResult.success) {
        setEditErrors({ _server: `Staff details saved, but the signature could not be saved: ${sigResult.error}` });
        return; // stay in edit mode so the admin can retry just the signature
      }
    } else {
      setIsUpdating(false);
    }

    cancelEdit();
    loadStaff();
  };

  // ── Disable / Enable Staff ──────────────────────────────────────

  const handleDisable = (staffId) => {
    setConfirmDialog({
      title: 'Disable Staff Member',
      message: 'This staff member will be logged out and cannot log in until re-enabled. Are you sure?',
      icon: '⚠️',
      variant: 'danger',
      ctaLabel: 'Disable',
      onConfirm: async () => {
        setConfirmDialog(null);
        setTogglingId(staffId);
        const result = await disableStaff(staffId);
        setTogglingId(null);

        if (result.success) {
          loadStaff();
        } else {
          setErrorAlert({ title: 'Failed to Disable', message: result.error });
        }
      },
    });
  };

  const handleEnable = async (staffId) => {
    setTogglingId(staffId);
    const result = await enableStaff(staffId);
    setTogglingId(null);

    if (result.success) {
      // Show new credentials so admin can share with re-enabled staff
      if (result.data?.temporary_password) {
        setCreatedCredentials([result.data]);
      }
      loadStaff();
    } else {
      setErrorAlert({ title: 'Failed to Enable', message: result.error });
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case STAFF_STATUS.ACTIVE:
        return { backgroundColor: 'rgba(34,197,94,0.15)', color: '#22c55e' };
      case STAFF_STATUS.DISABLED:
        return { backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444' };
      case STAFF_STATUS.INACTIVE:
        return { backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444' };
      case STAFF_STATUS.PENDING:
        return { backgroundColor: 'rgba(234,179,8,0.15)', color: '#eab308' };
      default:
        return { backgroundColor: 'rgba(156,163,175,0.15)', color: '#9ca3af' };
    }
  };

  // ── Dismiss on Escape ─────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || preventClose) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose, preventClose]);

  if (!isOpen) return null;

  // ── Render ────────────────────────────────────────────────────────

  return (
    <>
      <div
        className="manage-staff-overlay"
        onClick={preventClose ? undefined : onClose}
        style={{ backgroundColor: theme.background.overlay || 'rgba(0,0,0,0.5)' }}
      />
      <div
        className="manage-staff-modal"
        style={{ backgroundColor: theme.background.primary, color: theme.text.primary }}
      >
        {/* Header */}
        <div className="msm-header">
          <div>
            <h2 className="msm-title">Manage Staff</h2>
            {preventClose && (
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: theme.text.secondary }}>
                Please add at least one staff member to continue
              </p>
            )}
          </div>
          <div className="msm-header-actions">
            <button
              className="msm-add-btn"
              onClick={openAddForm}
              disabled={showAddForm}
              style={{ background: theme.gradient.userMessage, color: '#fff' }}
            >
              + Add Staff
            </button>
            {!preventClose && (
              <button
                className="msm-close-btn"
                onClick={onClose}
                style={{ color: theme.text.secondary }}
                aria-label="Close"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Scrollable body */}
        <div className="msm-body" ref={bodyRef}>
          {/* Add Form */}
          {showAddForm && (
            <div
              className="msm-add-section"
              style={{ borderColor: theme.border.default }}
            >
              <div className="msm-add-header">
                <h3 style={{ color: theme.text.primary }}>Add New Staff</h3>
                <button
                  className="msm-close-section"
                  onClick={closeAddForm}
                  style={{ color: theme.text.secondary }}
                >
                  ✕
                </button>
              </div>

              {submitError && (
                <div
                  className="staff-server-error"
                  style={{
                    backgroundColor: theme.brand.errorBg,
                    borderColor: theme.brand.error,
                    color: theme.brand.error,
                  }}
                >
                  <span className="staff-error-icon">⚠️</span>
                  <p>{submitError}</p>
                </div>
              )}

              <form onSubmit={handleAddSubmit} noValidate>
                {newStaffRows.map((row, idx) => (
                  <StaffFormRow
                    key={row._key}
                    data={row}
                    errors={newRowErrors[idx] || {}}
                    index={idx}
                    onChange={handleNewFieldChange}
                    onRemove={removeNewRow}
                    disabled={isSubmitting}
                    showRemove={newStaffRows.length > 1}
                    departments={departments}
                  />
                ))}

                <button
                  type="button"
                  className="staff-add-btn"
                  onClick={addNewRow}
                  disabled={isSubmitting}
                  style={{ color: theme.brand.purple, borderColor: theme.brand.purple }}
                >
                  <span className="staff-add-icon">+</span>
                  Add Another
                </button>

                <div className="msm-form-actions">
                  <button
                    type="button"
                    className="staff-skip-btn"
                    onClick={closeAddForm}
                    disabled={isSubmitting}
                    style={{ color: theme.text.secondary }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="staff-submit-btn"
                    disabled={isSubmitting}
                    style={{ background: theme.gradient.userMessage, color: '#fff' }}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="staff-spinner" />
                        Adding...
                      </>
                    ) : (
                      'Add Staff'
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="msm-loading" style={{ color: theme.text.secondary }}>
              <span className="staff-spinner" style={{ borderTopColor: theme.brand.purple }} />
              Loading staff...
            </div>
          )}

          {/* Error */}
          {listError && (
            <div
              className="staff-server-error"
              style={{
                backgroundColor: theme.brand.errorBg,
                borderColor: theme.brand.error,
                color: theme.brand.error,
              }}
            >
              <span className="staff-error-icon">⚠️</span>
              <p>{listError}</p>
            </div>
          )}

          {/* Staff Table */}
          {!isLoading && !listError && (
            <>
              {staffList.length === 0 ? (
                <div className="msm-empty" style={{ color: theme.text.secondary }}>
                  <p>No staff members yet. Click "+ Add Staff" to get started.</p>
                </div>
              ) : (
                <div className="msm-table-wrap">
                  <table
                    className="msm-table"
                    style={{ borderColor: theme.border.default }}
                  >
                    <thead>
                      <tr style={{ borderColor: theme.border.default }}>
                        <th style={{ color: theme.text.secondary }}>Name</th>
                        <th style={{ color: theme.text.secondary }}>Role</th>
                        <th style={{ color: theme.text.secondary }}>Specialization</th>
                        <th style={{ color: theme.text.secondary }}>Mobile</th>
                        <th style={{ color: theme.text.secondary }}>Email</th>
                        <th style={{ color: theme.text.secondary }}>Status</th>
                        <th style={{ color: theme.text.secondary }}>Username</th>
                        <th style={{ color: theme.text.secondary }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staffList.map((staff) => {
                        const memberId = staff.member_id || staff.staff_id || staff.id;
                        const isEditing = editingId === memberId;

                        if (isEditing) {
                          return (
                            <tr key={memberId} className="msm-edit-row">
                              <td colSpan={8}>
                                <div className="msm-edit-card">
                                  <StaffFormRow
                                    data={editData}
                                    errors={editErrors}
                                    index={0}
                                    onChange={handleEditFieldChange}
                                    showRemove={false}
                                    disabled={isUpdating}
                                    departments={departments}
                                    memberId={memberId}
                                    signatureLoading={editSignatureLoading}
                                  />
                                  {editErrors._server && (
                                    <p className="staff-field-error" style={{ marginTop: 8 }}>
                                      {editErrors._server}
                                    </p>
                                  )}
                                  <div className="msm-edit-actions">
                                    <button
                                      className="staff-skip-btn"
                                      onClick={cancelEdit}
                                      disabled={isUpdating}
                                      style={{ color: theme.text.secondary }}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      className="staff-submit-btn msm-save-btn"
                                      onClick={saveEdit}
                                      disabled={isUpdating}
                                      style={{ background: theme.gradient.userMessage, color: '#fff' }}
                                    >
                                      {isUpdating ? 'Saving...' : 'Save'}
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        return (
                          <tr key={memberId} style={{ borderColor: theme.border.default }}>
                            <td>
                              {staff.first_name} {staff.last_name}
                            </td>
                            <td>{STAFF_ROLE_LABELS[staff.role] || staff.role}</td>
                            <td style={{ fontSize: '0.8125rem' }}>
                              {staff.role === 'DOCTOR' && staff.specialization
                                ? (SPECIALIZATION_LABELS[staff.specialization] || staff.specialization)
                                : '—'}
                            </td>
                            <td>{staff.mobile}</td>
                            <td>{staff.email || '—'}</td>
                            <td>
                              <span
                                className="msm-status-badge"
                                style={getStatusBadgeStyle(staff.status)}
                              >
                                {STAFF_STATUS_LABELS[staff.status] || staff.status || 'Active'}
                              </span>
                            </td>
                            <td style={{ color: theme.text.secondary }}>
                              {staff.username || '—'}
                            </td>
                            <td>
                              <div className="msm-row-actions">
                                <button
                                  className="msm-action-btn"
                                  onClick={() => startEdit(staff)}
                                  disabled={staff.status === 'DISABLED'}
                                  style={{ color: theme.brand.purple, opacity: staff.status === 'DISABLED' ? 0.4 : 1 }}
                                >
                                  Edit
                                </button>
                                {staff.status === 'DISABLED' ? (
                                  <button
                                    className="msm-action-btn"
                                    onClick={() => handleEnable(memberId)}
                                    disabled={togglingId === memberId}
                                    style={{ color: '#22c55e' }}
                                  >
                                    {togglingId === memberId ? 'Enabling...' : 'Enable'}
                                  </button>
                                ) : (
                                  <button
                                    className="msm-action-btn msm-remove-btn"
                                    onClick={() => handleDisable(memberId)}
                                    disabled={togglingId === memberId || staff.role === 'ADMIN'}
                                    style={{ color: theme.brand.error, opacity: staff.role === 'ADMIN' ? 0.4 : 1 }}
                                    title={staff.role === 'ADMIN' ? 'Admin cannot be disabled' : ''}
                                  >
                                    {togglingId === memberId ? 'Disabling...' : 'Disable'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Credentials popup after adding staff */}
      <StaffCredentialsModal
        isOpen={createdCredentials.length > 0}
        credentials={createdCredentials}
        onClose={() => setCreatedCredentials([])}
      />

      {/* Confirm dialog (replaces window.confirm) */}
      {confirmDialog && (
        <ConfirmationModal
          isOpen={true}
          title={confirmDialog.title}
          message={confirmDialog.message}
          icon={confirmDialog.icon}
          variant={confirmDialog.variant}
          ctaLabel={confirmDialog.ctaLabel}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      {/* Error alert (replaces alert()) */}
      {errorAlert && (
        <ConfirmationModal
          isOpen={true}
          title={errorAlert.title}
          message={errorAlert.message}
          icon="❌"
          ctaLabel="OK"
          onConfirm={() => setErrorAlert(null)}
        />
      )}
    </>
  );
}

export default ManageStaffModal;
